import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '@/utils/api'
import { Button } from '@/components/ui/button'

export default function AdminReportsPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reportType, setReportType] = useState('users')
  const [summary, setSummary] = useState<unknown | null>(null)
  const [exportLink, setExportLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSummary = async () => {
    if (!startDate || !endDate) {
      setError('Select start and end dates')
      return
    }
    setLoading(true)
    setError(null)
    setExportLink(null)
    try {
      const res = await adminApi.reportsSummary({ startDate, endDate })
      setSummary((res as { data?: unknown }).data || res)
    } catch (err) {
      setError((err as Error)?.message || 'Failed to generate summary')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    if (!reportType) return
    setLoading(true)
    setError(null)
    try {
      const res = await adminApi.reportsExport({ type: reportType, format: 'json' })
      setExportLink(typeof res === 'string' ? res : JSON.stringify(res))
    } catch (err) {
      setError((err as Error)?.message || 'Failed to export')
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Reports'
          infoText='Summary and export endpoints.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 text-start mb-2'>Start Date</label>
            <input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              required
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 text-start mb-2'>End Date</label>
            <input
              type='date'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              required
            />
          </div>
        </div>

        <div className='flex flex-wrap gap-3'>
          {['users', 'organizations', 'couples', 'assessments', 'all'].map((type) => (
            <button
              key={type}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                reportType === type
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setReportType(type)}
              aria-pressed={reportType === type}
            >
              {type}
            </button>
          ))}
        </div>

        <div className='flex gap-3'>
          <Button
            className='bg-gray-900 hover:bg-gray-800'
            onClick={handleSummary}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Working...' : 'Generate Summary'}
          </Button>
          <Button
            variant='outline'
            className='border-orange-200 text-orange-600'
            onClick={handleExport}
            disabled={loading || !reportType}
          >
            Export
          </Button>
        </div>

        <div className='border border-gray-200 rounded-lg p-4 text-sm text-gray-800 bg-gray-50 space-y-3'>
          <p className='font-semibold text-gray-900'>Report Preview</p>
          {error && <p className='text-red-600 text-xs'>{error}</p>}
          {!summary && !exportLink && (
            <p className='text-gray-600 text-xs'>Generate a summary or export to see results.</p>
          )}

          {(summary && typeof summary === 'object' && (
            <div className='space-y-2'>
              {'period' in (summary as Record<string, unknown>) && (
                <div className='flex gap-4 text-xs text-gray-700'>
                  <div><span className='font-semibold'>Start:</span> {String((summary as any).period?.startDate || '')}</div>
                  <div><span className='font-semibold'>End:</span> {String((summary as any).period?.endDate || '')}</div>
                </div>
              )}
              {'summary' in (summary as Record<string, unknown>) && (
                <div className='grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs'>
                  {Object.entries((summary as any).summary || {}).map(([label, value]) => (
                    <div key={label} className='p-2 rounded-md bg-white border border-gray-200'>
                      <p className='font-semibold text-gray-900 capitalize'>{label.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className='text-xl font-bold text-gray-800 mt-1'>{String(value ?? 0)}</p>
                    </div>
                  ))}
                </div>
              )}
              {'userBreakdown' in (summary as Record<string, unknown>) && (
                <div className='text-xs'>
                  <p className='font-semibold text-gray-900 mb-1'>Users by role</p>
                  <div className='flex flex-wrap gap-2'>
                    {Object.entries((summary as any).userBreakdown?.byRole || {}).map(([role, count]) => (
                      <span key={role} className='px-2 py-1 rounded-md bg-white border border-gray-200'>
                        {role}: {count as string}
                      </span>
                    ))}
                    {Object.keys((summary as any).userBreakdown?.byRole || {}).length === 0 && (
                      <span className='text-gray-600'>No user breakdown.</span>
                    )}
                  </div>
                </div>
              )}
              {'organizationBreakdown' in (summary as Record<string, unknown>) && (
                <div className='text-xs'>
                  <p className='font-semibold text-gray-900 mb-1'>Organizations by status</p>
                  <div className='flex flex-wrap gap-2'>
                    {Object.entries((summary as any).organizationBreakdown?.byStatus || {}).map(([status, count]) => (
                      <span key={status} className='px-2 py-1 rounded-md bg-white border border-gray-200'>
                        {status}: {count as string}
                      </span>
                    ))}
                    {Object.keys((summary as any).organizationBreakdown?.byStatus || {}).length === 0 && (
                      <span className='text-gray-600'>No organization breakdown.</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )) as React.ReactNode}

          {exportLink && (
            <div className='text-xs text-gray-700'>
              <p className='font-semibold'>Export Result</p>
              <div className='mt-1 p-2 bg-white border border-gray-200 rounded-md break-words'>{exportLink}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
